import React, { useState, useEffect } from 'react';
import { Plus, X, Trash2 } from 'lucide-react';
import {
    TavolaAnatomica, SelettoreTavola, tavolaIniziale, chiaveTavola, scomponiTavola,
} from './TavolaAnatomica';
import { FraseRigaNuova } from '../StatoSalvataggio';
import { useUscitaProtetta } from '../../utils/uscitaProtetta';
import '../../styles/TavolaAnatomica.css';
import '../../styles/MappaAnatomica.css';
import '../../styles/MappaAnatomicaConsole.css';

/**
 * MappaAnatomica
 * Tavole anatomiche su cui segnare inestetismi, sintomi e trattamenti.
 *
 * Otto tavole: volto e corpo, maschile e femminile, superficiale e
 * muscolare. La scelta parte dal sesso del paziente ma resta
 * modificabile: un medico puo' voler lavorare sulla tavola muscolare o
 * su quella dell'altro sesso per una zona specifica, e obbligarlo
 * sarebbe un impedimento senza motivo.
 *
 * Ogni annotazione registra su quale tavola e' stata posata: le
 * immagini hanno proporzioni diverse, quindi un punto segnato sulla
 * mappa muscolare non cade nello stesso posto su quella superficiale.
 * Si mostrano quindi solo i punti della tavola in vista. Il disegno e
 * la misura del clic - percentuali dell'IMMAGINE, non del riquadro -
 * stanno in TavolaAnatomica, condivisa con lo schema delle zone
 * trattate della procedura: una implementazione sola, di proposito.
 *
 * Fronte e retro (o frontale e profilo) sono gia' affiancati dentro
 * l'immagine: non serve un selettore in piu', la posizione del punto
 * dice da sola a quale vista appartiene.
 */

const CATEGORIE = [
    { valore: 'dolore',        etichetta: 'Dolore',           colore: '#8a2f24' },
    { valore: 'infiammazione', etichetta: 'Infiammazione',    colore: '#7d5a15' },
    { valore: 'cicatrice',     etichetta: 'Cicatrice o segno', colore: '#3f6b4e' },
    { valore: 'trattamento',   etichetta: 'Trattamento',      colore: '#5f4780' },
    { valore: 'altro',         etichetta: 'Altro',            colore: '#6d6f71' },
];

const SEVERITA = ['lieve', 'moderata', 'grave'];

const coloreDi = (categoria) =>
    CATEGORIE.find((c) => c.valore === categoria)?.colore || '#6d6f71';

const MappaAnatomica = ({ pazienteId, sessoPaziente, annotazioni = [], onCreata, onEliminata }) => {
    // La tavola di partenza segue il sesso del paziente; da qui in poi
    // decide il medico.
    const [mappa, setMappa] = useState(tavolaIniziale(sessoPaziente));
    const { area } = scomponiTavola(mappa);

    const [nuovoPunto, setNuovoPunto] = useState(null);
    const [form, setForm] = useState({ categoria: 'dolore', descrizione: '', severita: 'lieve', data_inizio: '' });
    const [salvataggio, setSalvataggio] = useState(false);
    const [errore, setErrore] = useState(null);

    /* L'annotazione NUOVA resta un pulsante («Aggiungi» crea una riga
       firmata), ma quello che e' scritto non si perde in silenzio: domanda al
       cambio di schermata e avviso del browser (14 ottobre 2026). */
    useUscitaProtetta(!!nuovoPunto && !!(form.descrizione.trim() || form.data_inizio
        || form.categoria !== 'dolore' || form.severita !== 'lieve'));

    useEffect(() => {
        const { area: a, livello: l } = scomponiTavola(mappa);
        setMappa(chiaveTavola(a, sessoPaziente === 'M' ? 'm' : 'f', l));
        // eslint-disable-next-line
    }, [sessoPaziente]);

    // Solo i punti posati su QUESTA tavola: su un'altra cadrebbero
    // in una posizione anatomica diversa. Il filtro lo fa la tavola;
    // qui restano fuori le annotazioni spente.
    const attive = annotazioni.filter((a) => a.attivo !== false);
    const punti = attive.filter((a) => a.mappa === mappa);
    const perTavola = attive.map((a) => ({ ...a, x: a.coordinata_x, y: a.coordinata_y }));

    const handleClick = ({ x, y }) => {
        if (nuovoPunto) return; // una annotazione alla volta
        setNuovoPunto({ x, y });
        setForm({ categoria: 'dolore', descrizione: '', severita: 'lieve', data_inizio: '' });
        setErrore(null);
    };

    const salva = async () => {
        if (!form.descrizione.trim()) {
            setErrore('Descrivi cosa hai osservato in questo punto.');
            return;
        }
        setSalvataggio(true);
        setErrore(null);
        try {
            await onCreata({
                tipo: area === 'volto' ? 'volto' : 'corpo',
                mappa,
                coordinata_x: nuovoPunto.x,
                coordinata_y: nuovoPunto.y,
                categoria: form.categoria,
                descrizione: form.descrizione.trim(),
                severita: form.severita,
                colore: coloreDi(form.categoria),
                data_inizio: form.data_inizio || null,
                attivo: true,
            });
            setNuovoPunto(null);
        } catch (e) {
            setErrore(e?.message || 'Errore nel salvataggio');
        } finally {
            setSalvataggio(false);
        }
    };

    const elimina = async (a) => {
        if (!window.confirm(`Eliminare l'annotazione "${a.descrizione}"?`)) return;
        try {
            await onEliminata(a.id);
        } catch (e) {
            setErrore(e?.message || 'Errore nell\'eliminazione');
        }
    };

    return (
        <div className="ma-wrap">

            <div className="ma-comandi">
                <SelettoreTavola mappa={mappa} onCambia={setMappa} className="ma-" />

                <span className="ma-conteggio">
                    {punti.length === 0 ? 'Nessuna annotazione su questa tavola' :
                     punti.length === 1 ? '1 annotazione' : `${punti.length} annotazioni`}
                </span>
            </div>

            {errore && <div className="rm-messaggio rm-messaggio-errore ma-errore">{errore}</div>}

            <div className="ma-scena">
                <div className="ma-tavola">
                    <TavolaAnatomica mappa={mappa} punti={perTavola} onClick={handleClick}
                        className="ma-tavola-immagine"
                        rendiPunto={(a) => (
                            <button
                                className="ma-punto"
                                style={{ background: a.colore || coloreDi(a.categoria) }}
                                onClick={(e) => e.stopPropagation()}
                                title={`${a.descrizione}${a.severita ? ` (${a.severita})` : ''}`}
                            >
                                <span className="ma-punto-nota">
                                    <strong>{a.descrizione}</strong>
                                    {a.severita && <em>{a.severita}</em>}
                                    <span className="ma-punto-azioni">
                                        <span onClick={(e) => { e.stopPropagation(); elimina(a); }}>
                                            <Trash2 size={13} strokeWidth={1.8} aria-hidden="true" /> elimina
                                        </span>
                                    </span>
                                </span>
                            </button>
                        )}>
                        {nuovoPunto && (
                            <span className="ma-punto ma-punto-nuovo"
                                  style={{ left: `${nuovoPunto.x}%`, top: `${nuovoPunto.y}%` }} />
                        )}
                    </TavolaAnatomica>
                </div>

                {nuovoPunto ? (
                    <div className="ma-pannello">
                        <div className="ma-pannello-testa">
                            <h3>Nuova annotazione</h3>
                            <button className="ma-chiudi" onClick={() => setNuovoPunto(null)} aria-label="Annulla">
                                <X size={16} strokeWidth={1.8} />
                            </button>
                        </div>
                        <FraseRigaNuova pulsante="Aggiungi" />

                        <label className="ma-campo">
                            <span>Cosa hai osservato</span>
                            <textarea
                                rows={3}
                                value={form.descrizione}
                                onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                                placeholder="Es. cicatrice post-acneica, area di iperpigmentazione…"
                                autoFocus
                            />
                        </label>

                        <label className="ma-campo">
                            <span>Categoria</span>
                            <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
                                {CATEGORIE.map((c) => <option key={c.valore} value={c.valore}>{c.etichetta}</option>)}
                            </select>
                        </label>

                        <label className="ma-campo">
                            <span>Entità</span>
                            <select value={form.severita} onChange={(e) => setForm({ ...form, severita: e.target.value })}>
                                {SEVERITA.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </label>

                        <label className="ma-campo">
                            <span>Rilevata il</span>
                            <input type="date" value={form.data_inizio}
                                   onChange={(e) => setForm({ ...form, data_inizio: e.target.value })} />
                        </label>

                        <div className="ma-pannello-azioni">
                            <button className="rm-btn" onClick={() => setNuovoPunto(null)} disabled={salvataggio}>Annulla</button>
                            <button className="rm-btn rm-btn-primario" onClick={salva} disabled={salvataggio}>
                                {salvataggio ? 'Salvataggio…' : 'Aggiungi'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="ma-pannello ma-pannello-vuoto">
                        <Plus size={22} strokeWidth={1.5} aria-hidden="true" />
                        <p>Tocca un punto della tavola per aggiungere un'annotazione.</p>

                        {punti.length > 0 && (
                            <ul className="ma-elenco">
                                {punti.map((a) => (
                                    <li key={a.id}>
                                        <span className="ma-pallino" style={{ background: a.colore || coloreDi(a.categoria) }} />
                                        <span className="ma-elenco-testo">
                                            {a.descrizione}
                                            {a.severita && <em> · {a.severita}</em>}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            <div className="ma-legenda">
                {CATEGORIE.map((c) => (
                    <span key={c.valore}>
                        <span className="ma-pallino" style={{ background: c.colore }} />
                        {c.etichetta}
                    </span>
                ))}
            </div>
        </div>
    );
};

export default MappaAnatomica;
